import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useBids } from "@/hooks/useBids";

interface BidDialogProps {
  orderId: string;
  children: React.ReactNode;
}

export const BidDialog = ({ orderId, children }: BidDialogProps) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const { createBid, isCreating } = useBids();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return;
    }

    try {
      await createBid({
        order_id: orderId,
        amount: amountNum,
        message: message.trim() || undefined,
      });
      setAmount("");
      setMessage("");
      setOpen(false);
    } catch (error) {
      console.error("Error creating bid:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] bg-white rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            Soumettre une offre
          </DialogTitle>
          <DialogDescription className="text-[#5A6B7A] font-['Funnel_Sans']">
            Proposez votre prix pour cette commande
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label 
              htmlFor="amount" 
              className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide"
            >
              Montant proposé (€)
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="850.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 outline-none transition-all duration-200 placeholder:text-[#87ADD4] font-['Funnel_Sans']"
            />
          </div>

          <div className="space-y-2">
            <Label 
              htmlFor="message" 
              className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide"
            >
              Message (optionnel)
            </Label>
            <Textarea
              id="message"
              rows={4}
              placeholder="Ajoutez des détails sur votre offre..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 outline-none transition-all duration-200 placeholder:text-[#87ADD4] resize-none font-['Funnel_Sans']"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isCreating}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              {isCreating ? "Envoi..." : "Soumettre l'offre"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
